"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useSearchParams, useRouter } from "next/navigation";
import { useAuth, hasRole } from "@/lib/firebase/auth";
import {
  useMyCourses, useAssignedVerifications, useProgramCourses, useCourse,
  useUsersList, useAssignInstructor,
  useTQF3, useTQF5,
  useRemoveInstructorFromCourse,
  useVerificationReviewProgress,
  useCreateVerification,
  useProgramVerifications,
  currentAcademicYearShort, isInAcademicYear,
  courseDefaultSemesterId, normalizeSemesterId,
  type CourseLite,
} from "@/lib/firebase/queries";
import { useBulkAssignFromSchedule } from "@/lib/firebase/bulkAssign";
import {
  useMyPlannedReviews, useMyVerificationCourses, useVerificationPlan,
  type MyPlannedReview,
} from "@/lib/firebase/verificationPlan";
import { seedDemoData } from "@/lib/firebase/seed";
import { getActiveProgramId } from "@/lib/firebase/program-context";
import { useQueryClient } from "@tanstack/react-query";
import { OfferingPlanner } from "./OfferingPlanner";

const PROGRAM_ID = getActiveProgramId();

export default function DashboardPage() {
  return (
    <Suspense fallback={<div>กำลังโหลด...</div>}>
      <DashboardInner />
    </Suspense>
  );
}

function DashboardInner() {
  const sp = useSearchParams();
  const tab = sp.get("tab") ?? "";   // "", "instructor", "chair", "reviewer"
  // ──────────────────────────────────────────────────────────────────────────
  // Tab semantics (each menu shows ONLY its own section, never bleeds in):
  //   • "instructor" (or empty default) → 📚 รายวิชาของฉัน — courses assigned to me
  //   • "chair"                          → ⚙ หลักสูตร — 70-course manager (chair/admin only)
  //   • "reviewer"                       → ⚖ งานทวนสอบ — verification work
  // The course manager (chair view of all 70 courses) is the responsibility of
  // chair/admin and must NOT appear under "รายวิชาของฉัน".
  // ──────────────────────────────────────────────────────────────────────────
  const instructorTab = tab === "" || tab === "instructor";
  const chairTab = tab === "chair";
  const reviewerTab = tab === "reviewer";
  const { user, profile, loading } = useAuth();
  const qc = useQueryClient();
  const [seeding, setSeeding] = useState(false);
  const [seedMessage, setSeedMessage] = useState<string | null>(null);

  // ดึงทุกปี (ไม่กรองปีปัจจุบัน) — MyCoursesSection จัดการเลือกปีเอง
  const myCourses = useMyCourses(user?.uid ?? null, { onlyCurrentYear: false });
  const myReviews = useAssignedVerifications(user?.uid ?? null);
  const myPlannedReviews = useMyPlannedReviews(PROGRAM_ID, user?.uid ?? null);
  // "รายวิชาของคุณที่ถูกกำหนดให้ทวนสอบ" lives on the INSTRUCTOR tab (per chair:
  // instructors see their teaching + their courses scheduled for verification
  // in one place; the reviewer tab is purely committee work).
  const myVerificationCourses = useMyVerificationCourses(
    instructorTab ? PROGRAM_ID : "", // only fetch when on instructor tab
    instructorTab ? (user?.uid ?? null) : null
  );
  // All program verifications — lets the instructor view detect real verified
  // status by courseCode even when the plan's verificationId hasn't synced.
  const programVerifications = useProgramVerifications(instructorTab ? PROGRAM_ID : null);
  const allCourses = useProgramCourses(hasRole(profile, "chair") ? PROGRAM_ID : null);

  async function handleSeed() {
    if (!user || !profile) return;
    setSeeding(true);
    setSeedMessage(null);
    try {
      const result = await seedDemoData({
        uid: user.uid,
        displayName: profile.displayName,
        email: profile.email,
      });
      setSeedMessage(`✓ สร้าง ${result.created} เอกสาร, ข้าม ${result.skipped} (มีอยู่แล้ว)`);
      qc.invalidateQueries();
    } catch (e) {
      setSeedMessage(`✗ ${e instanceof Error ? e.message : "Seed failed"}`);
    } finally {
      setSeeding(false);
    }
  }

  if (loading || !profile) return null;

  const empty = (myCourses.data?.length ?? 0) === 0 && (myReviews.data?.length ?? 0) === 0 && (allCourses.data?.length ?? 0) === 0;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-brand-700">
          {reviewerTab
            ? "⚖ งานทวนสอบของฉัน"
            : chairTab
              ? "⚙ หลักสูตร — จัดการรายวิชาและอาจารย์ผู้สอน"
              : `ยินดีต้อนรับ ${profile.displayName}`}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {reviewerTab ? (
            <>เฉพาะรายวิชาที่ถูกกำหนดให้ทวนสอบ — ทั้งในฐานะ <strong>อาจารย์ผู้สอน</strong> และ <strong>คณะกรรมการทวนสอบ</strong></>
          ) : chairTab ? (
            <>กำหนดผู้สอน/ภาคที่เปิดสอน — สำหรับ <strong>ประธานหลักสูตร/admin</strong> เท่านั้น · เมื่อ assign แล้ว วิชาจะปรากฏใน &ldquo;รายวิชาของฉัน&rdquo; ของอาจารย์ท่านนั้น</>
          ) : (
            <>
              Role ของคุณ: {profile.roles.map(r => (
                <span key={r} className="inline-block ml-1 px-2 py-0.5 text-xs bg-brand-50 text-brand-700 rounded">{r}</span>
              ))}
            </>
          )}
        </p>
      </div>

      {empty && (
        <div className="rounded-xl border-2 border-dashed border-brand-300 bg-brand-50/30 p-6">
          <h3 className="font-semibold text-brand-700">🌱 ยังไม่มีข้อมูลในระบบ</h3>
          <p className="text-sm text-slate-600 mt-1">เริ่มต้นด้วย 1 ใน 2 ทาง:</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link href="/curriculum/upload" className="rounded-lg bg-brand-500 hover:bg-brand-600 text-white px-4 py-2 text-sm">
              📚 นำเข้าเล่มหลักสูตร (มคอ.2)
            </Link>
            <button
              onClick={handleSeed}
              disabled={seeding}
              className="rounded-lg border border-brand-300 hover:bg-brand-50 text-brand-700 px-4 py-2 text-sm disabled:opacity-50"
            >
              {seeding ? "..." : "หรือ Seed Demo Data"}
            </button>
          </div>
          {seedMessage && <div className="mt-2 text-sm">{seedMessage}</div>}
        </div>
      )}

      {/* Instructor section — my courses (only courses where I'm assigned).
          Shown ONLY on the "รายวิชาของฉัน" tab (or default). มีตัวเลือกปีการศึกษา */}
      {instructorTab && hasRole(profile, "instructor") && (
        <MyCoursesSection uid={user?.uid ?? null} courses={myCourses.data ?? []} />
      )}

      {/* Chair section — manage all 70 courses + assign instructors.
          Shown ONLY on the "หลักสูตร" tab — never on "รายวิชาของฉัน". */}
      {chairTab && hasRole(profile, "chair") && (
        <>
          <ChairCourseManager allCourses={allCourses.data ?? []} loading={allCourses.isLoading} />
          <OfferingPlanner programId={PROGRAM_ID} />
        </>
      )}
      {chairTab && !hasRole(profile, "chair") && (
        <div className="rounded-xl border-2 border-red-300 bg-red-50/30 p-6 text-center text-sm text-red-700">
          🔒 เมนูนี้สงวนสิทธิ์เฉพาะประธานหลักสูตร/admin
        </div>
      )}

      {/* Instructor tab: my courses scheduled for verification — lives HERE on
          "รายวิชาของฉัน" with a year selector (default = ปีปัจจุบัน, view past). */}
      {instructorTab && hasRole(profile, "instructor") && (
        <MyVerificationSection
          items={myVerificationCourses.data ?? []}
          verifications={programVerifications.data ?? []}
        />
      )}

      {/* Reviewer section (committee role): only shown when the user explicitly
          clicks the "งานทวนสอบ" menu (tab=reviewer). Hidden on "รายวิชาของฉัน"
          and "หลักสูตร" so each menu shows only its own content. */}
      {reviewerTab && hasRole(profile, "committee") && (() => {
        const currentYearShort = currentAcademicYearShort();
        const currentYearFull = 2500 + parseInt(currentYearShort, 10);

        // Filter verifications ปีปัจจุบัน (อิงจาก semester ของ verification)
        const currentReviews = (myReviews.data ?? []).filter(v => {
          if (!v.semesterId) return true;  // fallback: ไม่ระบุ semester = include
          return v.semesterId.endsWith("/" + currentYearShort);
        });
        const currentPlanned = (myPlannedReviews.data ?? []).filter(p => p.year === currentYearFull);

        if (currentReviews.length === 0 && currentPlanned.length === 0) return null;

        // Overall progress for THIS reviewer: a course counts as "ทวนสอบแล้ว"
        // when I have signed its VR-04. Match planned items to started
        // verifications by normalized courseCode.
        const signedByMeCodes = new Set(
          (myReviews.data ?? [])
            .filter(v => ((v as { signatures?: Array<{ uid: string }> }).signatures ?? []).some(s => s.uid === user?.uid))
            .map(v => (v.courseCode ?? "").replace(/\s+/g, ""))
        );
        const plannedNotStarted = currentPlanned.filter(p => !p.verificationId);
        const totalCourses = plannedNotStarted.length > 0 ? plannedNotStarted.length : currentReviews.length;
        const doneCourses = plannedNotStarted.length > 0
          ? plannedNotStarted.filter(p => signedByMeCodes.has(p.courseCode.replace(/\s+/g, ""))).length
          : currentReviews.filter(v => signedByMeCodes.has((v.courseCode ?? "").replace(/\s+/g, ""))).length;

        return (
        <section>
          <h2 className="text-lg font-semibold mb-1">
            ⚖ งานทวนสอบของฉัน <span className="text-xs text-slate-500">(ปีการศึกษา {currentYearFull})</span>
          </h2>
          {/* Progress bar: how many courses I have signed off */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="flex-1 min-w-[200px] max-w-md h-2.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all"
                style={{ width: `${totalCourses > 0 ? (doneCourses / totalCourses) * 100 : 0}%` }}
              />
            </div>
            <span className={`text-sm font-medium ${doneCourses === totalCourses && totalCourses > 0 ? "text-green-700" : "text-slate-600"}`}>
              ทวนสอบแล้ว {doneCourses}/{totalCourses} รายวิชา
              {doneCourses === totalCourses && totalCourses > 0 && " 🎉 ครบทุกวิชา!"}
            </span>
          </div>

          {/* Already-started verifications */}
          {currentReviews.length > 0 && (
            <div className="grid gap-3 mb-4">
              {currentReviews.map(v => {
                const signatures = ((v as { signatures?: Array<{ uid: string; name: string }> }).signatures ?? []);
                const iSigned = signatures.some(s => s.uid === user?.uid);
                return (
                <Link key={v.id} href={`/verification?id=${v.id}`} className="block rounded-xl border-2 border-green-300 bg-green-50/30 p-4 hover:border-green-500 transition">
                  <div className="flex justify-between items-start gap-2 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {v.status === "signed" || signatures.length > 0 ? (
                          <span className="text-xs px-2 py-0.5 bg-green-600 text-white rounded font-medium" title={`ลงนามแล้ว: ${signatures.map(s => s.name).join(", ")}`}>
                            ✍️ ลงนามแล้ว {signatures.length}/{v.committee?.length ?? 0} ท่าน
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">🚀 เริ่มแล้ว</span>
                        )}
                        {iSigned && (
                          <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded">✓ คุณลงนามแล้ว</span>
                        )}
                        <ReviewProgressBadge verifyId={v.id} />
                        <span className="font-mono text-xs bg-white border px-2 py-0.5 rounded">{v.courseCode}</span>
                      </div>
                      <div className="font-medium mt-1">{v.courseName}</div>
                      <div className="text-xs text-slate-500 mt-1">ภาค {v.semesterId ?? "—"}</div>
                    </div>
                    <span className="text-xs px-2 py-1 bg-brand-50 text-brand-700 rounded">{v.committee?.length ?? 0} กรรมการในคณะ</span>
                  </div>
                </Link>
                );
              })}
            </div>
          )}

          {/* Planned (upcoming, not yet started) — committee can start verification
              right here (per chair: แผนทวนสอบ is for planning only; the actual
              "เริ่มทวนสอบ" happens on the งานทวนสอบ page). */}
          {currentPlanned.filter(p => !p.verificationId).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-600 mb-2">📅 กำหนดการทวนสอบที่กำลังจะถึง</h3>
              <div className="grid gap-2">
                {currentPlanned.filter(p => !p.verificationId).map(p => (
                  <PlannedReviewCard key={`${p.year}-${p.courseCode}`} item={p} startedReviews={myReviews.data ?? []} />
                ))}
              </div>
              <div className="mt-2 text-[11px] text-slate-500">
                💡 กด &ldquo;🚀 เริ่มทวนสอบ&rdquo; เพื่อเปิดรอบทวนสอบด้วยคณะกรรมการประจำปี — กรรมการทุกท่านในชุดจะเข้าประเมินได้ทันที
              </div>
            </div>
          )}
        </section>
        );
      })()}
    </div>
  );
}

// ============ PlannedReviewCard — committee starts verification here ============

/**
 * One planned-but-not-yet-started verification on the งานทวนสอบ tab.
 * Per the chair's workflow: the plan page (แผนทวนสอบ) is chair-only planning;
 * the actual "เริ่มทวนสอบ" button lives HERE so any committee member can open
 * the verification round (using the plan-year committee) and start reviewing.
 */
function PlannedReviewCard({
  item: p,
  startedReviews,
}: {
  item: MyPlannedReview;
  startedReviews: Array<{
    id: string;
    courseCode?: string;
    semesterId?: string;
    signatures?: Array<{ uid: string; name: string }>;
    committee?: Array<{ uid: string }>;
  }>;
}) {
  const router = useRouter();
  const { user, profile } = useAuth();
  const plan = useVerificationPlan(PROGRAM_ID, p.year);
  const createVerification = useCreateVerification();

  // Duplicate guard: a verification for this course already exists (e.g. someone
  // else started it moments ago, or the plan's verificationId just hasn't synced)
  const existing = startedReviews.find(v => (v.courseCode ?? "").replace(/\s+/g, "") === p.courseCode.replace(/\s+/g, ""));
  const existingSignatures = existing?.signatures ?? [];
  // "ได้เข้าไปทวนสอบแล้ว" = current user has signed VR-04 of this course
  const iSigned = existingSignatures.some(s => s.uid === user?.uid);

  async function handleStart() {
    if (!user || !profile) return;
    const committeeList = plan.data?.committee ?? [];
    if (committeeList.length < 4 || !committeeList.some(m => m.role === "chair")) {
      alert("คณะกรรมการประจำปียังไม่ครบ (ต้อง ≥ 4 ท่าน และมีประธาน) — แจ้งประธานหลักสูตรตั้งกรรมการที่หน้า แผนทวนสอบ ก่อน");
      return;
    }
    const ok = confirm(
      `🚀 เริ่มทวนสอบ ${p.courseCode} ${p.courseName}?\n\n` +
      `คณะกรรมการประจำปี ${p.year} (${committeeList.length} ท่าน) จะเข้าประเมินได้ทันที`
    );
    if (!ok) return;
    // semesterId: "1/68" → "1-2568"
    const semesterId = normalizeSemesterId(p.semester) || p.semester;
    try {
      const result = await createVerification.mutateAsync({
        programId: PROGRAM_ID,
        courseId: p.courseId || p.courseCode.replace(/\s+/g, "-"),
        courseCode: p.courseCode,
        courseName: p.courseName,
        semesterId,
        scheduledDate: p.scheduledDate ? new Date(p.scheduledDate) : new Date(),
        committee: committeeList.map(m => ({ uid: m.uid, name: m.displayName, role: m.role })),
        createdByUid: user.uid,
      });
      router.push(`/verification?id=${result.verificationId}`);
    } catch (e) {
      alert("เริ่มไม่สำเร็จ: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  // Card tone follows MY progress: signed = green, started-not-signed = blue, untouched = white
  const cardTone = iSigned
    ? "border-2 border-green-400 bg-green-50/50"
    : existing
      ? "border-2 border-blue-300 bg-blue-50/30"
      : "border bg-white";

  return (
    <div className={`rounded-xl ${cardTone} p-3`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {iSigned ? (
              <span className="text-xs px-2 py-0.5 bg-green-600 text-white rounded font-medium">
                ✅ คุณทวนสอบและลงนามแล้ว
              </span>
            ) : existing ? (
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
                🚀 เริ่มแล้ว — รอคุณเข้าประเมิน
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">⏳ ยังไม่เริ่ม</span>
            )}
            {existing && existingSignatures.length > 0 && (
              <span
                className="text-[11px] px-2 py-0.5 bg-white border border-green-300 text-green-700 rounded"
                title={`ลงนามแล้ว: ${existingSignatures.map(s => s.name).join(", ")}`}
              >
                ✍️ ลงนาม {existingSignatures.length}/{existing.committee?.length ?? "?"} ท่าน
              </span>
            )}
            <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{p.courseCode}</span>
            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{p.semester}</span>
            <span className="text-[11px] text-slate-500">บทบาท: {roleLabel(p.roleInCommittee)}</span>
          </div>
          <div className="font-medium mt-1">{p.courseName}</div>
          {p.scheduledDate ? (
            <div className="text-xs text-slate-600 mt-1">📅 นัดทวนสอบ: <strong>{formatThaiDate(p.scheduledDate)}</strong></div>
          ) : (
            <div className="text-xs text-amber-600 mt-1">⚠ ยังไม่กำหนดวันที่ทวนสอบ</div>
          )}
          {p.reason && <div className="text-xs text-slate-500 mt-0.5">{p.reason}</div>}
        </div>
        <div className="shrink-0">
          {iSigned ? (
            <Link
              href={`/verification?id=${existing!.id}`}
              className="text-xs rounded-md border border-green-400 bg-white text-green-700 hover:bg-green-50 px-3 py-1.5 inline-block"
            >
              ดูผลทวนสอบ →
            </Link>
          ) : existing ? (
            <Link
              href={`/verification?id=${existing.id}`}
              className="text-xs rounded-md bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 inline-block"
            >
              เข้าประเมิน →
            </Link>
          ) : (
            <button
              onClick={handleStart}
              disabled={createVerification.isPending || plan.isLoading}
              className="text-xs rounded-md bg-brand-500 hover:bg-brand-600 text-white px-3 py-1.5 disabled:opacity-50"
              title="เปิดรอบทวนสอบด้วยคณะกรรมการประจำปี"
            >
              {createVerification.isPending ? "กำลังเปิดรอบ..." : "🚀 เริ่มทวนสอบ"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Chair: course management with instructor assignment ============

function ChairCourseManager({ allCourses, loading }: { allCourses: CourseLite[]; loading: boolean }) {
  const usersList = useUsersList();
  const assign = useAssignInstructor();
  const bulkAssign = useBulkAssignFromSchedule();
  const { user, profile } = useAuth();

  const [filter, setFilter] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState<"all" | "assigned" | "unassigned">("all");
  // Default: hide legacy / old-curriculum courses (2562/2567 ก่อนหน้า). The
  // current curriculum is 2567 — legacy courses are tagged category="legacy"
  // (or their nameTh contains "หลักสูตรเก่า"). Toggle to reveal when needed.
  const [showLegacy, setShowLegacy] = useState(false);

  async function handleBulkAssign() {
    if (!user || !profile) return;
    const ok = confirm(
      "Bulk Assign ทุกรายวิชาจากแผนเปิดรายวิชา 2568?\n\n" +
      "ระบบจะ:\n" +
      "1. สร้าง user docs สำหรับอาจารย์ 13 ท่าน (ที่ยังไม่ได้ login)\n" +
      "2. Assign instructorUid ให้ ~30 รายวิชา\n" +
      "3. เมื่ออาจารย์จริง login → ระบบ link ให้อัตโนมัติ\n\n" +
      "ดำเนินการ?"
    );
    if (!ok) return;
    try {
      const result = await bulkAssign.mutateAsync({
        programId: PROGRAM_ID,
        currentUserUid: user.uid,
        currentUserName: profile.displayName,
      });
      alert(
        `✓ Bulk Assign สำเร็จ!\n\n` +
        `สร้างผู้ใช้ ${result.usersCreated.length} ท่าน\n` +
        `Assign รายวิชา ${result.assignmentsApplied} ตัว\n` +
        (result.skippedNotFound.length > 0 ? `ไม่พบในระบบ ${result.skippedNotFound.length} รายวิชา: ${result.skippedNotFound.join(", ")}` : "")
      );
    } catch (e) {
      alert("Bulk Assign ล้มเหลว: " + (e instanceof Error ? e.message : String(e)));
      console.error("BulkAssign error:", e);
    }
  }

  const legacyCount = allCourses.filter(isLegacyCourse).length;
  // Current-curriculum courses (exclude legacy unless toggled)
  const currentCourses = showLegacy ? allCourses : allCourses.filter(c => !isLegacyCourse(c));

  const filtered = currentCourses.filter(c => {
    if (filter && !(c.code.includes(filter) || c.nameTh.includes(filter))) return false;
    if (assignmentFilter === "assigned" && !c.instructorUid) return false;
    if (assignmentFilter === "unassigned" && c.instructorUid) return false;
    return true;
  });

  const stats = {
    total: currentCourses.length,
    assigned: currentCourses.filter(c => c.instructorUid).length,
    unassigned: currentCourses.filter(c => !c.instructorUid).length,
  };

  async function handleAssign(courseId: string, instructorUid: string) {
    const user = usersList.data?.find(u => u.uid === instructorUid);
    if (!user) return;
    await assign.mutateAsync({
      programId: PROGRAM_ID,
      courseId,
      instructorUid: user.uid,
      instructorName: user.displayName,
    });
  }

  async function handleUnassign(courseId: string) {
    await assign.mutateAsync({
      programId: PROGRAM_ID,
      courseId,
      instructorUid: "",
      instructorName: "",
    });
  }

  return (
    <section>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <h2 className="text-lg font-semibold">
          🎓 หลักสูตร — รายวิชา{showLegacy ? "ทั้งหมด" : " (ฉบับปัจจุบัน 2567)"} ({stats.total})
        </h2>
        <div className="flex gap-2">
          <button
            onClick={handleBulkAssign}
            disabled={bulkAssign.isPending}
            className="text-sm rounded-lg bg-brand-500 hover:bg-brand-600 text-white px-3 py-1.5 disabled:opacity-50"
            title="Assign อาจารย์อัตโนมัติจากแผนเปิดรายวิชา 2568"
          >
            {bulkAssign.isPending ? "กำลัง assign..." : "📥 Bulk Assign จากแผน 2568"}
          </button>
          <Link href="/curriculum" className="text-sm rounded-lg border px-3 py-1.5 hover:bg-slate-50">📚 เล่มหลักสูตร</Link>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat label="รายวิชาทั้งหมด" value={stats.total} />
        <Stat label="อาจารย์ assigned แล้ว" value={stats.assigned} accent="success" />
        <Stat label="ยังไม่มีอาจารย์" value={stats.unassigned} accent="warning" />
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-3 flex-wrap">
        <input
          type="text"
          placeholder="🔍 ค้นหารหัส/ชื่อ..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border rounded-md px-3 py-1.5 text-sm flex-1 min-w-[200px]"
        />
        <select
          value={assignmentFilter}
          onChange={(e) => setAssignmentFilter(e.target.value as "all" | "assigned" | "unassigned")}
          className="border rounded-md px-3 py-1.5 text-sm"
        >
          <option value="all">ทั้งหมด</option>
          <option value="unassigned">ยังไม่มีอาจารย์</option>
          <option value="assigned">มีอาจารย์แล้ว</option>
        </select>
        {legacyCount > 0 && (
          <label className="flex items-center gap-1.5 text-xs text-slate-600 border rounded-md px-3 py-1.5 cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              checked={showLegacy}
              onChange={(e) => setShowLegacy(e.target.checked)}
            />
            แสดงรายวิชาหลักสูตรเก่า ({legacyCount})
          </label>
        )}
      </div>

      {loading && <div className="rounded-xl border bg-white p-6 text-slate-500">กำลังโหลด...</div>}

      {/* Course table */}
      <div className="rounded-xl border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="p-2 text-left w-28">รหัส</th>
              <th className="p-2 text-left">ชื่อรายวิชา</th>
              <th className="p-2 text-left w-24">ประเภท</th>
              <th className="p-2 text-left w-64">อาจารย์ผู้รับผิดชอบ</th>
              <th className="p-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} className="border-t">
                <td className="p-2 font-mono text-xs">{c.code}</td>
                <td className="p-2">
                  <div>{c.nameTh}</div>
                  <div className="text-xs text-slate-400">{(c as CourseLite & { nameEn?: string }).nameEn ?? ""}</div>
                </td>
                <td className="p-2 text-xs">
                  <span className="px-1.5 py-0.5 bg-slate-100 rounded">{(c as CourseLite & { category?: string }).category ?? "—"}</span>
                </td>
                <td className="p-2">
                  <AssignCell
                    course={c}
                    users={usersList.data ?? []}
                    onAssign={(uid) => handleAssign(c.id, uid)}
                    onUnassign={() => handleUnassign(c.id)}
                    busy={assign.isPending}
                  />
                </td>
                <td className="p-2">
                  <Link href={`/course?id=${c.id}`} className="text-brand-700 hover:underline text-xs">ดู</Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="p-8 text-center text-slate-400 text-sm">
                  {allCourses.length === 0
                    ? "ยังไม่มีรายวิชาในระบบ — นำเข้าเล่มหลักสูตรก่อนเพื่อ sync รายวิชาเข้ามา"
                    : "ไม่พบรายวิชาที่ตรงเงื่อนไข"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AssignCell({
  course, users, onAssign, onUnassign, busy,
}: {
  course: CourseLite;
  users: { uid: string; displayName: string; email: string }[];
  onAssign: (uid: string) => void;
  onUnassign: () => void;
  busy: boolean;
}) {
  const sections = course.sections ?? [];
  const hasMultipleSections = sections.length > 1;
  const uniqueInstructors = Array.from(new Set(sections.map(s => s.instructorName)));

  if (hasMultipleSections) {
    return (
      <div className="text-xs">
        <div className="font-medium text-sm">
          {uniqueInstructors.length} อาจารย์ · {sections.length} sections
        </div>
        <details>
          <summary className="cursor-pointer text-brand-700 text-xs hover:underline">ดู sections</summary>
          <div className="mt-1 space-y-0.5">
            {sections.map(s => (
              <div key={s.section} className="text-[11px] text-slate-600">
                sec {s.section}: {s.instructorName}
              </div>
            ))}
          </div>
        </details>
      </div>
    );
  }

  if (course.instructorUid) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="text-sm">{course.instructorName}</div>
        </div>
        <button onClick={onUnassign} disabled={busy} className="text-xs text-red-600 hover:underline disabled:opacity-50">เปลี่ยน</button>
      </div>
    );
  }
  return (
    <select
      onChange={(e) => e.target.value && onAssign(e.target.value)}
      defaultValue=""
      disabled={busy}
      className="w-full border rounded-md px-2 py-1 text-sm disabled:opacity-50"
    >
      <option value="">— เลือกอาจารย์ —</option>
      {users.map(u => (
        <option key={u.uid} value={u.uid}>{u.displayName} ({u.email})</option>
      ))}
    </select>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: "success" | "warning" }) {
  const tone = accent === "success" ? "text-green-600" : accent === "warning" ? "text-amber-600" : "text-brand-700";
  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

// ============ ScheduledVerificationCard (instructor tab) ============

/** Small badge showing how many committee members have reviewed a verification.
 *  Business rule: ≥1 reviewer = the course counts as "ทวนสอบแล้ว". */
function ReviewProgressBadge({ verifyId }: { verifyId: string | undefined }) {
  const progress = useVerificationReviewProgress(PROGRAM_ID, verifyId);
  if (!verifyId || progress.isLoading) return null;
  const p = progress.data;
  if (!p) return null;
  if (p.hasReviewed) {
    return (
      <span
        className="text-xs px-2 py-0.5 bg-green-600 text-white rounded font-medium"
        title={`กรรมการที่ทวนสอบแล้ว: ${p.reviewerNames.join(", ")}`}
      >
        ✓ ทวนสอบแล้ว ({p.reviewerCount} กรรมการ)
      </span>
    );
  }
  return (
    <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded">
      ยังไม่มีกรรมการทวนสอบ
    </span>
  );
}

type ScheduledItem = {
  year: number;
  courseId: string;
  courseCode: string;
  courseName: string;
  semester: string;
  reason: string;
  scheduledDate: string;
  verificationId?: string;
  verificationStatus: "planned" | "in_progress" | "completed";
};

type VerifLite = { id: string; courseCode?: string; committee?: unknown[]; signatures?: Array<{ uid: string; name: string }>; status?: string };

// ============ MyVerificationSection — รายวิชาที่ถูกกำหนดให้ทวนสอบ + ตัวเลือกปี ============

function MyVerificationSection({ items, verifications }: { items: ScheduledItem[]; verifications: VerifLite[] }) {
  const currentYearFull = 2500 + parseInt(currentAcademicYearShort(), 10);
  // ปีที่มีในแผน + ปีปัจจุบัน → เรียงใหม่→เก่า
  const years = [...new Set([currentYearFull, ...items.map(i => i.year)])].sort((a, b) => b - a);
  const [selYear, setSelYear] = useState<number | null>(null);
  const effYear = selYear ?? years[0] ?? currentYearFull;

  if (items.length === 0) return null; // ไม่เคยถูกกำหนดทวนสอบเลย → ซ่อน

  // map courseCode → verification (เลือกตัวที่มีลายเซ็นก่อน)
  const verifsByCode = new Map<string, { id: string; signatures: Array<{ uid: string; name: string }>; committee: number; status: string }>();
  for (const v of verifications) {
    const code = (v.courseCode ?? "").replace(/\s+/g, "");
    if (!code) continue;
    const sigs = v.signatures ?? [];
    const prev = verifsByCode.get(code);
    if (!prev || (sigs.length > 0 && prev.signatures.length === 0)) {
      verifsByCode.set(code, { id: v.id, signatures: sigs, committee: v.committee?.length ?? 0, status: v.status ?? "" });
    }
  }

  const myCs = items.filter(i => i.year === effYear);
  const withStatus = myCs.map(c => {
    const match = verifsByCode.get(c.courseCode.replace(/\s+/g, ""));
    return { c, match, verified: (match?.signatures.length ?? 0) > 0 };
  });
  const pending = withStatus.filter(x => !x.verified);
  const done = withStatus.filter(x => x.verified);

  return (
    <section>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h2 className="text-lg font-semibold">
          🎓 รายวิชาของคุณที่ถูกกำหนดให้ทวนสอบ
          {myCs.length > 0 && (
            <span className="text-sm font-normal text-slate-500 ml-2">
              (ทวนสอบแล้ว {done.length}/{myCs.length} · ปี {effYear})
            </span>
          )}
        </h2>
        {years.length > 1 && (
          <div className="flex items-center gap-2 text-sm">
            <label className="text-slate-600">ปีการศึกษา:</label>
            <select
              value={effYear}
              onChange={(e) => setSelYear(parseInt(e.target.value))}
              className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}{y === currentYearFull ? " (ปัจจุบัน)" : ""}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {myCs.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/30 p-6 text-center text-sm text-slate-500">
          ปีการศึกษา {effYear} ยังไม่ได้กำหนดแผนทวนสอบสำหรับวิชาของคุณ — ประธานหลักสูตรจะกำหนดที่เมนู &ldquo;แผนทวนสอบ&rdquo;
        </div>
      ) : (
        <>
          {/* Progress bar */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="flex-1 min-w-[200px] max-w-md h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-green-500 transition-all" style={{ width: `${(done.length / myCs.length) * 100}%` }} />
            </div>
            {done.length === myCs.length && (
              <span className="text-sm font-medium text-green-700">🎉 ทวนสอบครบทุกวิชาแล้ว</span>
            )}
          </div>

          {/* Pending first */}
          <div className="grid gap-3">
            {pending.map(({ c, match }) => (
              <ScheduledVerificationCard key={`${c.year}-${c.courseId}`} item={c} matchedVerifyId={match?.id} signatures={match?.signatures ?? []} committeeCount={match?.committee ?? 0} />
            ))}
          </div>

          {/* Verified */}
          {done.length > 0 && (
            <div className="mt-4">
              <div className="text-sm font-medium text-green-700 mb-2">✅ ทวนสอบแล้ว ({done.length} รายวิชา)</div>
              <div className="grid gap-2">
                {done.map(({ c, match }) => (
                  <ScheduledVerificationCard key={`${c.year}-${c.courseId}`} item={c} matchedVerifyId={match?.id} signatures={match?.signatures ?? []} committeeCount={match?.committee ?? 0} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 text-[11px] text-slate-500">
            💡 ในฐานะอาจารย์เจ้าของวิชา — เตรียม มคอ.3/มคอ.5 ให้พร้อมก่อนวันนัดทวนสอบ
          </div>
        </>
      )}
    </section>
  );
}

function ScheduledVerificationCard({
  item: c,
  matchedVerifyId,
  signatures,
  committeeCount,
}: {
  item: ScheduledItem;
  matchedVerifyId?: string;
  signatures: Array<{ uid: string; name: string }>;
  committeeCount: number;
}) {
  // Prefer the real verification matched by courseCode (passed in) over the
  // plan's possibly-stale verificationId.
  const verifyId = matchedVerifyId ?? c.verificationId;
  const isStarted = !!verifyId || c.verificationStatus === "in_progress";
  const verified = signatures.length > 0;

  const tone = verified
    ? "border-green-400 bg-green-50/40"
    : isStarted
      ? "border-blue-300 bg-blue-50/30"
      : "border-amber-300 bg-amber-50/30";

  return (
    <div className={`rounded-xl border-2 ${tone} p-4`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {verified ? (
              <span
                className="text-xs px-2 py-0.5 bg-green-600 text-white rounded font-medium"
                title={`ลงนามโดย: ${signatures.map(s => s.name).join(", ")}`}
              >
                ✅ ทวนสอบแล้ว · ลงนาม {signatures.length}{committeeCount > 0 ? `/${committeeCount}` : ""} ท่าน
              </span>
            ) : isStarted ? (
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">🚀 กำลังทวนสอบ</span>
            ) : (
              <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">⏳ รอเริ่มทวนสอบ</span>
            )}
            <span className="font-mono text-xs bg-white border px-2 py-0.5 rounded">{c.courseCode}</span>
            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">ภาค {c.semester}</span>
            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">แผนปี {c.year}</span>
          </div>
          <div className="font-medium mt-2">{c.courseName}</div>
          {c.scheduledDate ? (
            <div className="text-xs text-slate-600 mt-1">📅 นัดทวนสอบ: <strong>{formatThaiDate(c.scheduledDate)}</strong></div>
          ) : (
            <div className="text-xs text-amber-600 mt-1">⚠ ยังไม่ระบุวันที่นัด</div>
          )}
          {c.reason && <div className="text-[11px] text-slate-500 mt-0.5 italic">เหตุผล: {c.reason}</div>}
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          {/* Always include ?sem= of the course's actual offered semester
              (normalized from "1/68" → "1-2568") so the TQF3/5 editor opens
              the right semester doc, not the global default. */}
          <Link
            href={`/course?id=${c.courseId}&sem=${normalizeSemesterId(c.semester) || ""}`}
            className="text-xs rounded-md border border-blue-300 bg-white hover:bg-blue-50 text-blue-700 px-3 py-1.5 text-center"
          >
            📝 มคอ.3
          </Link>
          <Link
            href={`/tqf5?id=${c.courseId}&sem=${normalizeSemesterId(c.semester) || ""}`}
            className="text-xs rounded-md border border-amber-300 bg-white hover:bg-amber-50 text-amber-700 px-3 py-1.5 text-center"
          >
            📊 มคอ.5
          </Link>
          {isStarted && verifyId && (
            <Link
              href={`/verification?id=${verifyId}`}
              className={
                "text-xs rounded-md px-3 py-1.5 text-center " +
                (verified
                  ? "border border-green-400 bg-white text-green-700 hover:bg-green-50"
                  : "bg-green-600 hover:bg-green-700 text-white")
              }
            >
              {verified ? "ดูผลทวนสอบ →" : "ดูทวนสอบ →"}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ MyCoursesSection — รายวิชาของฉัน + ตัวเลือกปีการศึกษา ============

function MyCoursesSection({ uid, courses }: { uid: string | null; courses: CourseLite[] }) {
  // รวมปีการศึกษาที่อาจารย์มี section
  const yearSet = new Set<string>();
  for (const c of courses) {
    for (const s of (c.sections ?? [])) {
      if (s.instructorUid === uid && s.semester) {
        const y = s.semester.split("/")[1];
        if (y) yearSet.add(y);
      }
    }
  }
  const years = [...yearSet].sort((a, b) => b.localeCompare(a)); // ล่าสุดก่อน
  const [selYear, setSelYear] = useState<string>("");
  const effYear = selYear || years[0] || currentAcademicYearShort();
  const fullYear = (y: string) => 2500 + (parseInt(y, 10) || 0);

  // กรองรายวิชาตามปีที่เลือก (รวมรายวิชา legacy ที่ไม่มี section ระบุ)
  const shown = courses.filter(c => {
    const mySecs = (c.sections ?? []).filter(s => s.instructorUid === uid);
    if (mySecs.length === 0) return true;
    return mySecs.some(s => isInAcademicYear(s.semester, effYear));
  });

  return (
    <section>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h2 className="text-lg font-semibold">
          📚 รายวิชาของฉัน
          <span className="text-sm font-normal text-slate-500 ml-2">
            ({shown.length} รายวิชา · ปีการศึกษา {fullYear(effYear)})
          </span>
        </h2>
        {years.length > 1 && (
          <div className="flex items-center gap-2 text-sm">
            <label className="text-slate-600">ปีการศึกษา:</label>
            <select
              value={effYear}
              onChange={(e) => setSelYear(e.target.value)}
              className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white"
            >
              {years.map(y => (
                <option key={y} value={y}>{fullYear(y)}{y === years[0] ? " (ล่าสุด)" : ""}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      {courses.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/30 p-6 text-center text-sm text-slate-500">
          ยังไม่มีรายวิชาที่ถูก assign ให้คุณ — ประธานหลักสูตรจะ assign ผ่านเมนู &ldquo;หลักสูตร&rdquo; เมื่อพร้อม
        </div>
      ) : shown.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/30 p-6 text-center text-sm text-slate-500">
          ปีการศึกษา {fullYear(effYear)} ยังไม่มีรายวิชาที่คุณเปิดสอน — เลือกปีอื่นจาก dropdown ด้านบน
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {shown.map(c => (
            <InstructorCourseCard key={c.id} courseId={c.id} code={c.code} nameTh={c.nameTh} credit={c.credit} yearShort={effYear} />
          ))}
        </div>
      )}
    </section>
  );
}

// ============ InstructorCourseCard with TQF3/5 status ============

const FALLBACK_SEMESTER_ID = "2-2568"; // only used if course has no sections

function InstructorCourseCard({ courseId, code, nameTh, credit, yearShort }: {
  courseId: string; code: string; nameTh: string; credit: number | string; yearShort?: string;
}) {
  const { user } = useAuth();
  // Need the course doc to know which semester it's taught in (each course
  // has its own offered semester via course.sections[].semester) — limited to
  // the selected academic year when given.
  const courseQ = useCourse(PROGRAM_ID, courseId);
  const semesterId = courseDefaultSemesterId(courseQ.data, user?.uid, yearShort) || FALLBACK_SEMESTER_ID;
  const tqf3 = useTQF3(PROGRAM_ID, courseId, semesterId);
  const tqf5 = useTQF5(PROGRAM_ID, courseId, semesterId);
  const removeMe = useRemoveInstructorFromCourse();

  async function handleNotMine(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    const mySections = (courseQ.data?.sections ?? []).filter(s => s.instructorUid === user.uid);
    const ok = confirm(
      `🚫 แจ้ง "ไม่ได้สอน" รายวิชา ${code}\n\n` +
      `ระบบจะลบคุณออกจาก ${mySections.length} section ของรายวิชานี้\n` +
      `(${mySections.map(s => `Sec ${s.section}`).join(", ")})\n\n` +
      `รายวิชานี้จะหายจาก "รายวิชาของฉัน" ทันที — ดำเนินการ?`
    );
    if (!ok) return;
    try {
      const res = await removeMe.mutateAsync({
        programId: PROGRAM_ID, courseId, uid: user.uid,
      });
      alert(`✓ ลบออก ${res.removedSectionCount} section เรียบร้อย`);
    } catch (err) {
      alert("ลบไม่สำเร็จ: " + (err instanceof Error ? err.message : String(err)));
    }
  }

  const status = tqf3.data?.status ?? tqf5.data?.status ?? "draft";
  const tqf3Submitted = ["tqf3_submitted", "in_progress", "tqf5_submitted", "verifying", "verified"].includes(status);
  const tqf5Submitted = ["tqf5_submitted", "verifying", "verified"].includes(status);
  const tqf3HasContent = (tqf3.data?.clos?.length ?? 0) > 0;
  const tqf5HasContent = (tqf5.data?.cloAchievement?.length ?? 0) > 0
    || (tqf5.data?.sectionResults?.length ?? 0) > 0;

  return (
    <Link href={`/course?id=${courseId}&sem=${semesterId}`} className="block rounded-xl border bg-white p-4 hover:border-brand-500 transition">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xs font-mono text-slate-500">{code}</div>
            {semesterId && (
              <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">
                ภาค {semesterId}
              </span>
            )}
          </div>
          <div className="font-medium mt-1">{nameTh}</div>
          <div className="text-xs text-slate-500 mt-1">{credit} หน่วยกิต</div>
        </div>
        <button
          onClick={handleNotMine}
          disabled={removeMe.isPending}
          className="shrink-0 text-[10px] text-slate-400 hover:text-red-600 hover:underline disabled:opacity-50"
          title="ลบตัวเองออกจากรายวิชานี้ (สำหรับกรณีถูก assign ผิด)"
        >
          {removeMe.isPending ? "..." : "🚫 ไม่ได้สอน"}
        </button>
      </div>

      {/* Status badges */}
      <div className="flex items-center gap-1.5 mt-3 flex-wrap text-[11px]">
        {/* TQF3 */}
        {tqf3Submitted ? (
          <span className="rounded-full px-2 py-0.5 bg-green-100 text-green-700 font-medium">
            ✓ มคอ.3 ส่งแล้ว
          </span>
        ) : tqf3HasContent ? (
          <span className="rounded-full px-2 py-0.5 bg-amber-100 text-amber-700">
            📝 มคอ.3 (draft)
          </span>
        ) : (
          <span className="rounded-full px-2 py-0.5 bg-slate-100 text-slate-500">
            มคอ.3 ยังไม่เริ่ม
          </span>
        )}
        {/* TQF5 */}
        {tqf5Submitted ? (
          <span className="rounded-full px-2 py-0.5 bg-green-100 text-green-700 font-medium">
            ✓ มคอ.5 ส่งแล้ว
          </span>
        ) : tqf5HasContent ? (
          <span className="rounded-full px-2 py-0.5 bg-amber-100 text-amber-700">
            📊 มคอ.5 (draft)
          </span>
        ) : tqf3Submitted ? (
          <span className="rounded-full px-2 py-0.5 bg-blue-50 text-blue-600">
            มคอ.5 รอเปิดภาค
          </span>
        ) : null}
        {/* Multi-section indicator */}
        {(tqf5.data?.sectionResults?.length ?? 0) > 0 && (
          <span className="rounded-full px-2 py-0.5 bg-purple-50 text-purple-700">
            🏫 {tqf5.data?.sectionResults?.length} ห้อง
          </span>
        )}
      </div>
    </Link>
  );
}

/** A course belongs to an OLD curriculum (2562/earlier) if it's tagged
 *  category "legacy" or its Thai name explicitly says "หลักสูตรเก่า". The
 *  current active curriculum is 2567 — these should be hidden by default. */
function isLegacyCourse(c: { category?: string; nameTh?: string }): boolean {
  if ((c.category ?? "").toLowerCase() === "legacy") return true;
  if ((c.nameTh ?? "").includes("หลักสูตรเก่า")) return true;
  return false;
}

function roleLabel(r: "chair" | "internal" | "external" | "secretary"): string {
  return r === "chair" ? "ประธาน"
    : r === "external" ? "ภายนอก"
    : r === "secretary" ? "เลขานุการ"
    : "กรรมการ";
}

function formatThaiDate(iso: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const months = ["ม.ค.","ก.พ.","มี.ค.","เม.ย.","พ.ค.","มิ.ย.","ก.ค.","ส.ค.","ก.ย.","ต.ค.","พ.ย.","ธ.ค."];
    return `${d.getDate()} ${months[d.getMonth()]} ${(d.getFullYear() + 543).toString().slice(-2)}`;
  } catch { return iso; }
}
